// Portuguese copy for /face-data-policy (hand-written from copy.en.ts; same shape, same facts). Brazilian Portuguese,
// like /face-data-changes-2026. The detailed documents it links to are in English.
import type { FaceDataPolicyCopy } from "./copy.en";

const CONTACT =
  "CLOUDCONCUR LTD (empresa n.º 13399913), operando como Photohawk, 23 Caldey Gardens, Ingleby Barwick, Stockton-on-Tees, TS17 5HW, Reino Unido. E-mail: support@photohawk.com.";

const pt: FaceDataPolicyCopy = {
  seo: {
    title: "Política de dados faciais | Photohawk",
    description:
      "Quais dados faciais a Photohawk cria, onde são tratados, para que são usados e quando são excluídos: uma política para o Photohawk Studio (galerias de eventos e empresas) e outra para o app Photohawk (o app para as fotos da sua gente).",
  },
  title: "Política de dados faciais",
  version: "1.1",
  effectiveDate: "22 de outubro de 2026",
  meta: "Versão {version} · Em vigor desde {date}",
  intro: [
    "A Photohawk tem dois produtos que encontram as pessoas nas fotos, e cada um tem sua própria política de dados faciais abaixo. O Photohawk Studio administra galerias de eventos e empresas, onde cada pessoa encontra as suas fotos com uma selfie. O app Photohawk é para as fotos da sua gente, no seu próprio celular.",
    "Os dois são mantidos separados. Os dados faciais das galerias do Photohawk Studio e os do app Photohawk nunca são compartilhados, misturados ou comparados entre si. Nenhum deles é vendido ou usado para publicidade, e dados faciais nunca são uma foto: excluí-los nunca exclui uma foto.",
  ],
  tabsLabel: "Escolha um produto",
  studio: {
    id: "studio",
    tab: "Photohawk Studio",
    heading: "Photohawk Studio — galerias de eventos e empresas",
    summary:
      "A busca por selfie é opcional e é ativada pelo administrador de cada galeria. Ela permite que uma pessoa encontre as suas próprias fotos naquela galeria com uma selfie.",
    sections: [
      {
        heading: "1. Quais dados faciais são criados",
        blocks: [
          {
            type: "ul",
            items: [
              "Modelos faciais: representações numéricas dos rostos detectados nas fotos de uma galeria, guardadas no índice de busca facial daquela galeria (Amazon Rekognition) ou, nas galerias que usam um dos nossos próprios modelos faciais, como vetores faciais.",
              "O arquivo de rostos: pequenos recortes de rostos guardados de forma privada para que a busca facial de uma galeria possa ser reconstruída sem ler de novo as fotos originais. Nunca é mostrado a ninguém nem publicado.",
              "As selfies usadas para buscar não são dados faciais que guardamos: uma selfie é comparada com os modelos faciais daquela galeria e depois descartada. Ela não é guardada e nenhum modelo facial é criado a partir dela.",
            ],
          },
          {
            type: "p",
            text: "Encontrar pessoas nas fotos (corpos inteiros, sem identificar ninguém) e ler números de peito e textos não usa dados faciais. A busca por número de peito não é afetada por esta política.",
          },
        ],
      },
      {
        heading: "2. Onde são tratados",
        blocks: [
          {
            type: "ul",
            items: [
              "Na nuvem da Photohawk, na Amazon Web Services na União Europeia (Irlanda, eu-west-1), depois que uma foto é enviada.",
              "Cada galeria tem o seu próprio índice de busca facial (uma coleção do Amazon Rekognition). Uma selfie só é buscada na galeria para a qual foi tirada.",
              "Para ganhar tempo, os apps de envio do Photohawk Studio podem encontrar os rostos no computador ou no celular que envia as fotos e mandar pequenos recortes de rostos junto com a foto. Os modelos faciais são sempre criados na nossa nuvem.",
            ],
          },
        ],
      },
      {
        heading: "3. Para que são usados e para que nunca são usados",
        blocks: [
          {
            type: "p",
            text: "Os dados faciais são usados apenas para que as pessoas encontrem as suas próprias fotos naquela galeria com uma selfie.",
          },
          {
            type: "p",
            text: "Eles nunca são vendidos, nunca são usados para publicidade, nunca são usados para identificar alguém fora da galeria e nunca são misturados ou comparados com os dados faciais do app Photohawk.",
          },
        ],
      },
      {
        heading: "4. Por quanto tempo são guardados e quando são excluídos",
        blocks: [
          {
            type: "p",
            text: "A partir de 1 de novembro de 2026, os dados faciais são excluídos de forma permanente no primeiro destes momentos:",
          },
          {
            type: "ul",
            items: [
              "12 meses depois do envio da foto (foto a foto, mesmo que a galeria continue online);",
              "30 dias depois que a galeria é fechada ou excluída, ou que a sua busca facial é desativada (cancelado se o administrador reabrir a galeria ou reativar a busca facial nesses 30 dias);",
              "30 dias depois que uma pessoa nos pede para excluir os seus dados faciais ou retira o seu consentimento;",
              "45 dias depois que a nossa revisão anual concluir que eles não são mais necessários.",
            ],
          },
          {
            type: "p",
            text: "Até 31 de outubro de 2026, os dados faciais são excluídos quando uma galeria expira (em cerca de 60 dias) e quando uma galeria é excluída. Os modelos faciais não têm backup, então uma exclusão é definitiva; os backups do nosso banco de dados guardam apenas referências a rostos (nunca modelos) e são apagados em até 35 dias. Fotos, vendas, downloads e a busca por número de peito não são afetados.",
          },
          {
            type: "link",
            text: "O calendário de conservação completo e como funciona a exclusão (em inglês):",
            label: "Biometric data retention policy",
            href: "/biometric-data-retention",
          },
        ],
      },
      {
        heading: "5. Base legal",
        blocks: [
          {
            type: "p",
            text: "Fotos. As fotos de uma galeria são compartilhadas para que os participantes possam encontrar e comprar as suas próprias fotos. Elas são tratadas porque isso é necessário para a finalidade da galeria, e guardadas apenas pelo tempo proporcional a essa finalidade (veja o §4).",
          },
          {
            type: "p",
            text: "Busca facial. Dados faciais são dados biométricos e recebem proteção extra:",
          },
          {
            type: "ul",
            items: [
              "Quando são usados: apenas para que as pessoas possam encontrar as suas próprias fotos naquela galeria. Nunca são usados para identificar ninguém para qualquer outra finalidade.",
              "Por quanto tempo são guardados: apenas por um tempo limitado (§4). São excluídos assim que deixam de ser necessários.",
              "A pessoa que busca é convidada a dar o seu consentimento explícito antes de a selfie dela ser usada. A selfie nunca é guardada.",
              "O administrador decide se a busca facial é adequada para o seu evento e se é permitida no local onde ele acontece. Quando a lei local exige o consentimento das pessoas que aparecem nas fotos, o administrador precisa tê-lo, por exemplo por meio da inscrição no evento. Se não o tiver, deve usar a busca por número de peito e por horário, que não usa dados faciais.",
              "Qualquer pessoa pode pedir a qualquer momento que os seus dados faciais sejam excluídos, e eles são excluídos em até 30 dias (§8).",
            ],
          },
          {
            type: "p",
            text: "Para as pessoas de uma galeria, o administrador da galeria é o controlador; a Photohawk trata os dados faciais em nome dele, com o nosso acordo de tratamento de dados.",
          },
        ],
      },
      {
        heading: "6. Illinois e outros estados dos EUA",
        blocks: [
          {
            type: "p",
            text: "Esta política e a política de conservação de dados biométricos são o nosso calendário de conservação público e por escrito. Os limites de exclusão do §4 estão dentro dos prazos da lei de Illinois sobre informações biométricas (BIPA: quando a finalidade é cumprida ou em até 3 anos desde a última interação da pessoa), da CUBI do Texas, da Lei de Privacidade do Colorado e da RCW 19.375 de Washington. O limite de 12 meses é uma escolha da própria Photohawk; nenhuma lei o estabelece.",
          },
        ],
      },
      {
        heading: "7. Quem os trata",
        blocks: [
          {
            type: "p",
            text: "Amazon Web Services (Amazon Rekognition e armazenamento), região UE (Irlanda), como nosso suboperador, apenas segundo as nossas instruções. Não revelamos dados faciais a mais ninguém, a menos que a lei exija.",
          },
        ],
      },
      {
        heading: "8. Pedidos e contato",
        blocks: [
          {
            type: "ul",
            items: [
              "Na galeria: abra a busca por selfie e escolha “Make a request”. O pedido vai para o administrador da galeria e para a Photohawk.",
              "Por e-mail: support@photohawk.com, dizendo a qual galeria ou evento se refere. Atendemos o pedido em até 30 dias.",
              CONTACT,
            ],
          },
        ],
      },
    ],
    full: {
      text: "O calendário de conservação detalhado do Studio (em inglês):",
      label: "Biometric data retention policy",
      href: "/biometric-data-retention",
    },
  },
  family: {
    id: "family",
    tab: "App Photohawk",
    heading: "O app Photohawk — para as fotos da sua gente",
    summary:
      "O agrupamento de rostos é opcional. O app (nas lojas como “Photohawk”) pede permissão antes de criar dados faciais, e os rostos são encontrados no seu celular.",
    sections: [
      {
        heading: "1. Quais dados faciais são criados",
        blocks: [
          {
            type: "ul",
            items: [
              "Com o agrupamento de rostos ativado, no seu celular: modelos faciais (uma lista de números para cada rosto encontrado), onde estão os rostos em cada foto ou vídeo e quais rostos pertencem à mesma pessoa. Os nomes e as correções que você adiciona ficam apenas no seu celular.",
              "Se você também ativar a comparação de rostos na nuvem: pequenos recortes de rostos (nunca fotos ou vídeos inteiros) e os modelos faciais e grupos que o nosso serviço na nuvem cria a partir deles.",
            ],
          },
          {
            type: "p",
            text: "Encontrar pessoas (corpos inteiros, sem identificar ninguém) e coisas nas suas fotos não usa dados faciais e funciona com o agrupamento de rostos desativado.",
          },
        ],
      },
      {
        heading: "2. Onde são tratados",
        blocks: [
          {
            type: "ul",
            items: [
              "No seu celular: os rostos são encontrados no celular, e os dados faciais ficam no armazenamento privado do app, fora dos backups do celular.",
              "Na nuvem, apenas com o seu consentimento à comparação de rostos na nuvem: os recortes de rostos vão para a sua própria coleção de rostos no Amazon Rekognition (Amazon Web Services, região UE (Irlanda)), que guarda apenas os seus rostos. Os recortes são excluídos assim que são comparados (o que sobrar é excluído depois de um dia).",
              "Sem esse consentimento, tudo fica no seu celular. Nunca recebemos as suas fotos ou vídeos, nem os nomes que você dá às pessoas.",
            ],
          },
        ],
      },
      {
        heading: "3. Para que são usados e para que nunca são usados",
        blocks: [
          {
            type: "p",
            text: "Os dados faciais são usados apenas para agrupar as suas fotos e vídeos pelas pessoas que aparecem neles, para você buscar por pessoa e para ajudar você a escolher quais fotos compartilhar.",
          },
          {
            type: "p",
            text: "Eles nunca são vendidos, nunca são usados para publicidade, criação de perfis ou verificação de identidade, nunca são usados para identificar alguém fora das suas próprias fotos, nunca são comparados entre usuários diferentes, nunca são usados para treinar modelos e nunca são misturados ou comparados com os dados faciais do Photohawk Studio. Nunca fazem parte de um compartilhamento.",
          },
        ],
      },
      {
        heading: "4. Por quanto tempo são guardados e quando são excluídos",
        blocks: [
          {
            type: "p",
            text: "No seu celular, os dados faciais ficam guardados até você desativar o agrupamento de rostos (ou tocar em “Delete all face data”), excluir o app ou ficar 3 anos sem usar a Photohawk. Os dados faciais de uma foto são excluídos quando a foto sai do seu celular.",
          },
          {
            type: "p",
            text: "Na nuvem, os dados faciais são excluídos quando você desativa a comparação de rostos na nuvem ou o agrupamento de rostos, quando exclui a sua conta Photohawk, quando nos pede (em até 30 dias), depois de 3 anos sem usar a Photohawk e 30 dias depois do fim do seu plano, a menos que você assine de novo antes disso.",
          },
          {
            type: "p",
            text: "A exclusão na nuvem apaga toda a sua coleção de rostos. Excluir dados faciais nunca exclui fotos ou vídeos.",
          },
        ],
      },
      {
        heading: "5. Base legal e consentimento",
        blocks: [
          {
            type: "p",
            text: "Usamos como base o seu consentimento explícito (art. 9.º, n.º 2, alínea a) do RGPD e o equivalente em outras leis), pedido duas vezes e separadamente: primeiro para o agrupamento de rostos no seu celular (a caixa começa desmarcada; “Not now” significa que nenhum rosto é detectado) e depois para a comparação de rostos na nuvem (“Continue” ou “Keep it on my phone only”). Você pode mudar qualquer um dos dois a qualquer momento nas configurações do app. Se o texto mudar, o app pergunta de novo.",
          },
        ],
      },
      {
        heading: "6. Illinois",
        blocks: [
          {
            type: "p",
            text: "O agrupamento de rostos, e portanto a comparação de rostos na nuvem, não é oferecido a quem informa ao app que mora em Illinois (o app pergunta; não usa GPS nem o seu endereço IP). O nosso serviço também recusa a comparação na nuvem para Illinois, e se você mudar o lugar onde mora para Illinois, os dados faciais existentes são excluídos no celular e na nuvem.",
          },
        ],
      },
      {
        heading: "7. Quem os trata",
        blocks: [
          {
            type: "p",
            text: "Apenas com a comparação de rostos na nuvem: Amazon Web Services (Amazon Rekognition), região UE (Irlanda), como nosso operador, apenas segundo as nossas instruções. Os dados faciais do seu celular nunca são enviados a ninguém.",
          },
        ],
      },
      {
        heading: "8. Pedidos e contato",
        blocks: [
          {
            type: "ul",
            items: [
              "No app: desative a comparação de rostos na nuvem ou o agrupamento de rostos, ou toque em “Delete all face data”.",
              "Por e-mail: support@photohawk.com, para acessar ou excluir os seus dados faciais na nuvem.",
              CONTACT,
            ],
          },
        ],
      },
    ],
    full: {
      text: "A política de dados faciais completa do app Photohawk, com o seu calendário de conservação (em inglês):",
      label: "Photohawk app: Face Data Policy",
      href: "/face-data-policy/family",
    },
  },
  changelog: {
    title: "Alterações",
    items: [
      "1.1, 22 de outubro de 2026: Photohawk Studio, seção 5 reescrita: qual é a base legal para as fotos e para a busca facial, e o que o administrador decide.",
      "1.0, 20 de outubro de 2026: primeira versão. Uma página com uma política própria para o Photohawk Studio e outra para o Photohawk Family; a política de dados faciais do Photohawk Family deixa de ser um rascunho.",
    ],
  },
  seeAlso: {
    text: "Como os dados faciais são tratados no Photohawk Studio e no app Photohawk:",
    label: "Política de dados faciais",
  },
  familyPage: {
    seoTitle: "Photohawk app: Face Data Policy | Photohawk",
    description:
      "A política de dados faciais completa do app Photohawk: quais dados faciais ele cria, onde ficam guardados e o seu calendário de conservação e destruição.",
    back: "Política de dados faciais dos dois produtos",
    englishOnly: "Este documento está disponível apenas em inglês.",
  },
};

export default pt;
